// pages/PersonalData/components/Step3Phones/Step3Phones.tsx
import React, { useState, useEffect } from 'react';
import { PersonalData } from "features/personal-data";
import DataList from '../DataList/DataList';
import Button from 'shared/ui/Button/Button';
import { Button_Style, Button_Type } from 'shared/ui/Button/Button';
import { ReactComponent as IconAdd } from 'assets/img/icons/icon_Plus.svg';
import { ReactComponent as IconInfo } from 'assets/img/icons/icon_Info.svg';
import './Step3Phones.scss';
import { normalizePhoneNumber } from "shared/lib/phoneNumbers_utils";

interface Step3PhonesProps {
  existingData: PersonalData[];
  onAdd: (value: string) => Promise<void>;
  onUpdate: (id: number, value: string) => Promise<void>;
  onDelete: (id: number) => Promise<void>;
  onNext: () => void;
  onSkip: () => void;
  onBack: () => void;
}

const Step3Phones: React.FC<Step3PhonesProps> = ({
                                                   existingData,
                                                   onAdd,
                                                   onUpdate,
                                                   onDelete,
                                                   onNext,
                                                   onSkip,
                                                   onBack
                                                 }) => {
  const [newPhone, setNewPhone] = useState('');
  const [isValid, setIsValid] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [showForm, setShowForm] = useState(existingData.length === 0);
  const [normalizedPhone, setNormalizedPhone] = useState('');

  // Validation en temps réel
  useEffect(() => {
    if (!newPhone) {
      setIsValid(true);
      setErrorMessage('');
      setNormalizedPhone('');
      return;
    }

    const normalized = normalizePhoneNumber(newPhone);

    if (normalized) {
      setIsValid(true);
      setErrorMessage('');
      setNormalizedPhone(normalized);
    } else {
      setIsValid(false);
      setErrorMessage('Format de téléphone invalide (ex: +33612345678)');
      setNormalizedPhone('');
    }
  }, [newPhone]);

  const handleAddPhone = async () => {
    if (!newPhone || !isValid || !normalizedPhone) return;

    setIsAdding(true);
    try {
      await onAdd(normalizedPhone);
      setNewPhone('');
      setShowForm(false);
    } catch (error) {
      console.error('Erreur lors de l\'ajout:', error);
    } finally {
      setIsAdding(false);
    }
  };

  const handleAddAnother = () => {
    setShowForm(true);
  };

  const canProceed = existingData.length > 0; // Au moins un téléphone valide

  return (
    <div className="step3-phones">
      <div className="step3-phones__header">
        <h2 className="step3-phones__title">
          Protégez vos numéros de téléphone
        </h2>
        <p className="step3-phones__subtitle">
          Ajoutez les numéros que vous souhaitez surveiller
        </p>
      </div>

      {/* Liste des téléphones existants */}
      {existingData.length > 0 && (
        <div className="step3-phones__list">
          <h3 className="step3-phones__list-title">
            Vos numéros protégés
          </h3>
          <DataList
            items={existingData}
            type="phoneNumber"
            onEdit={onUpdate}
            onDelete={onDelete}
          />
        </div>
      )}

      {/* Formulaire d'ajout */}
      {showForm ? (
        <div className="step3-phones__form">
          <h3 className="step3-phones__form-title">
            {existingData.length === 0
              ? 'Ajoutez votre premier numéro'
              : 'Ajouter un nouveau numéro'}
          </h3>

          <div className="step3-phones__input-group">
            <input
              type="tel"
              value={newPhone}
              onChange={(e) => setNewPhone(e.target.value)}
              placeholder="+33612345678"
              className={`
                                step3-phones__input
                                ${!isValid && newPhone ? 'step3-phones__input--error' : ''}
                            `}
              autoFocus
            />

            {errorMessage && (
              <span className="step3-phones__error">{errorMessage}</span>
            )}

            {normalizedPhone && isValid && (
              <span className="step3-phones__normalized">
                                Format normalisé : {normalizedPhone}
                            </span>
            )}
          </div>

          <div className="step3-phones__form-actions">
            <Button
              label="Ajouter"
              type={Button_Type.PRIMARY}
              style={Button_Style.SOLID}
              onClick={handleAddPhone}
              disabled={!newPhone || !isValid || isAdding}
              className="step3-phones__add-btn"
            />

            {existingData.length > 0 && (
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="step3-phones__cancel-btn"
              >
                Annuler
              </button>
            )}
          </div>
        </div>
      ) : (
        // Bouton pour ajouter un autre numéro
        <div className="step3-phones__add-more">
          <button
            type="button"
            onClick={handleAddAnother}
            className="step3-phones__add-more-btn"
          >
            <IconAdd className="step3-phones__add-more-icon" />
            <span>Ajouter un autre numéro</span>
          </button>
        </div>
      )}

      {/* Message d'information */}
      {!canProceed && (
        <div className="step3-phones__info">
          <IconInfo className="step3-phones__info-icon" />
          <span className="step3-phones__info-text">
                        Ajoutez au moins un numéro pour continuer
                    </span>
        </div>
      )}

      {/* Actions de navigation */}
      <div className="step3-phones__navigation">
        <Button
          label="Retour"
          type={Button_Type.SECONDARY}
          style={Button_Style.OUTLINED}
          onClick={onBack}
          className="step3-phones__nav-btn"
        />

        <div className="step3-phones__nav-right">
          <Button
            label="Passer"
            type={Button_Type.SECONDARY}
            style={Button_Style.OUTLINED}
            onClick={onSkip}
            className="step3-phones__nav-btn"
          />

          <Button
            label="Continuer"
            type={Button_Type.PRIMARY}
            style={Button_Style.SOLID}
            onClick={onNext}
            disabled={!canProceed}
            className="step3-phones__nav-btn step3-phones__nav-btn--primary"
          />
        </div>
      </div>
    </div>
  );
};

export default Step3Phones;